import Vue from "vue";
import VueRouter from "vue-router";

// Routes
import { canNavigate } from "@/libs/acl/routeProtection";
import {
  isUserLoggedIn,
  getHomeRouteForLoggedInUser,
} from "@/auth/utils";
import apps from "./routes/apps";
import dashboard from "./routes/dashboard";
import uiElements from "./routes/ui-elements/index";
import pages from "./routes/pages";
import chartsMaps from "./routes/charts-maps";
import formsTable from "./routes/forms-tables";
import others from "./routes/others";
import store from "@/store/index";
// import axios from "axios";

Vue.use(VueRouter);

// Domain-specific redirect configuration
const REGISTRATION_DOMAIN = "applyatksa"; // Supports both localhost and production domain
const REGISTRATION_URL =
  "/student-registration?db=/r6Q0RYpjkeTUi0oFvmLqQ==&cId=1";

// Define a custom navigation permission function that respects meta.proceed
const checkRoutePermission = (to) => {
  // If any route in the matched routes has meta.proceed=true, allow navigation
  if (to.matched.some((route) => route.meta && route.meta.proceed === true)) {
    console.log("Proceed route detected");
    return true;
  }

  // Otherwise, use the standard canNavigate function
  return canNavigate(to);
};

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
  routes: [
    { path: "/", redirect: { name: "auth-login" } },
    ...apps,
    ...dashboard,
    ...pages,
    ...chartsMaps,
    ...formsTable,
    ...uiElements,
    ...others,
    {
      path: "*",
      redirect: "error-404",
    },
  ],
});

// Domain-specific redirect to handle the registration page redirect
router.beforeEach((to, from, next) => {
  console.log(
    "Router guard running for path:",
    to.path,
    "on domain:",
    window.location.hostname
  );

  // Check for specific domain that needs special handling
  const currentHostname = window.location.hostname;
  const shouldRedirect = currentHostname.includes(REGISTRATION_DOMAIN);

  // Check if this is a login route that should be redirected on specific domain
  if (shouldRedirect) {
    console.log("Domain match found!");
    if (to.path === "/" || to.name === "auth-login") {
      console.log("Redirecting to registration page...");

      // Only redirect if not already on registration URL to avoid loops
      if (to.path !== REGISTRATION_URL) {
        // Option 1: Use Vue Router (works within SPA context)
        next({ path: REGISTRATION_URL, replace: true });
        return;
      }
    }
  }

  const isLoggedIn = isUserLoggedIn();

  // Use our custom permission function instead of direct canNavigate
  if (!checkRoutePermission(to)) {
    console.log("cant navigate block");

    // Redirect to login if not logged in
    if (!isLoggedIn) return next({ name: "auth-login" });

    // If logged in => not authorized
    return next({ name: "misc-not-authorized" });
  }

  // Redirect if logged in.
  // Never assume menu[0] exists: after a deploy / cleared storage the persisted
  // menu can be empty, and throwing here used to abort navigation (splash stuck
  // forever at "/").
  if (to.meta.redirectIfLoggedIn && isLoggedIn) {
    const menu = Array.isArray(store.state.menu) ? store.state.menu : [];
    const firstGroup = menu.find(
      (group) => group && Array.isArray(group.children) && group.children.length
    );
    const firstChild =
      firstGroup &&
      firstGroup.children.find((child) => child && child.route);
    const fallbackRoute = firstChild
      ? firstChild.route
      : "dashboard-ecommerce";

    next({
      name: getHomeRouteForLoggedInUser(fallbackRoute),
    });
  }
  // console.log(store.state.quizInstance);
  // if (to.name == "quiz") {
  //   store.commit("setInstance", 0);
  //   console.log(store.state.quizInstance);
  // }
  // console.log(to);
  return next();
});

// ? For splash screen
const hideSplash = () => {
  // Soft cover first → wait for UI → enable glass blur → then fade out
  const appLoading = document.getElementById("loading-bg");
  if (!appLoading || appLoading.dataset.hiding === "1") return;
  appLoading.dataset.hiding = "1";

  try {
    sessionStorage.removeItem("myskool-splash-retry");
  } catch (error) {
    /* ignore */
  }

  const appRoot = document.getElementById("app");
  const startedAt = Date.now();
  const maxWaitMs = 1800;

  const finishHide = () => {
    appLoading.classList.add("is-hiding");
    setTimeout(() => {
      appLoading.style.display = "none";
    }, 180);
  };

  const enableGlassThenHide = () => {
    // App UI is under the overlay — turn on real frosted glass
    appLoading.classList.add("is-ready");
    requestAnimationFrame(() => {
      setTimeout(finishHide, 140);
    });
  };

  const waitForAppPaint = () => {
    const hasUi = appRoot && appRoot.childElementCount > 0;
    const timedOut = Date.now() - startedAt > maxWaitMs;
    if (hasUi || timedOut) {
      if (hasUi) {
        // Let nav/layout paint one more beat before glass
        setTimeout(enableGlassThenHide, 120);
      } else {
        finishHide();
      }
      return;
    }
    setTimeout(waitForAppPaint, 40);
  };

  waitForAppPaint();
};

router.afterEach(() => {
  hideSplash();
});

// A failed lazy chunk (common right after a new deploy) used to abort
// navigation before afterEach, leaving the splash forever. Hide it, then retry
// once with a fresh document so the new hashed chunks can load.
router.onError((error) => {
  hideSplash();
  const message = String((error && error.message) || error || "");
  const chunkFailed = /Loading chunk|ChunkLoadError|Loading CSS chunk/i.test(
    message
  );

  if (!chunkFailed) return;

  try {
    if (sessionStorage.getItem("myskool-chunk-reload") === "1") return;
    sessionStorage.setItem("myskool-chunk-reload", "1");
  } catch (sessionError) {
    return;
  }

  window.location.reload();
});

// Clear the chunk-reload marker as soon as a navigation succeeds.
router.afterEach(() => {
  try {
    sessionStorage.removeItem("myskool-chunk-reload");
  } catch (error) {
    /* ignore */
  }
});

export default router;
